import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CreditCard, ExternalLink, Sparkles, LogOut, ShieldCheck, Check, PauseCircle, Gift } from "lucide-react";
import { toast } from "sonner";

// Feature catalog per tier — pulled from the actual implemented feature set,
// NOT marketing copy.  Keep in sync with setup_stripe.py product descriptions.
//
// PROCESS RULE — READ ME BEFORE MERGING NEW FEATURES:
// Every time a new user-facing feature ships (dashboard widget, gate, hardware
// hook, integration, etc.), append it to the correct tier's `features` array
// in the SAME PR/commit that ships the feature.  This is how the Billing
// radar panel stays honest about what a customer is paying for.  Do NOT ship
// a feature without updating this catalog — it is the customer-visible source
// of truth and drives self-serve upgrade conversion.
// Deep-link targets — where does each feature route to in the app?
// Some features (e.g. Priority support) have no discrete UI target, so href
// is omitted and those rows render as non-interactive info.
const R = {
  receipts: "/receipts",
  ingestion: "/ingestion",
  bankFeed: "/bank-feed",
  scan: "/scan",
  enterprise: "/enterprise",
  paycheckOptimizer: "/dashboard#paycheck-optimizer-node",
  auditShield: "/dashboard#audit-shield-row",
  quarterlyVault: "/dashboard#quarterly-tax-vault-row",
  dashboard: "/dashboard",
};

const TIER_CONFIG = {
  pro: {
    displayName: "Pro Plan",
    priceLabel: "$9.99/mo",
    accent: "#10B981",
    accentBg: "rgba(16,185,129,0.12)",
    accentBorder: "rgba(16,185,129,0.35)",
    features: [
      { label: "Unlimited OCR receipt scanning (GPT-5.2 Vision)", href: R.scan },
      { label: "IRS-compliant digital archiving", href: R.receipts },
      { label: "Smart categorization & space-age search", href: R.receipts },
      { label: "Export to PDF & CSV", href: R.receipts },
      { label: "Email inbox + SMS ingestion", href: R.ingestion },
    ],
  },
  business: {
    displayName: "Business Plan",
    priceLabel: "$19.99/mo + $7.99/seat",
    accent: "#10B981",
    accentBg: "rgba(16,185,129,0.12)",
    accentBorder: "rgba(16,185,129,0.35)",
    downgradeTo: { lookup_key: "pro_monthly", label: "Move to Pro", targetName: "Pro", targetPrice: "$9.99/mo", losses: [
      "Multi-tenant workspaces & team seats",
      "Amazon / Walmart / DoorDash sync",
      "Smart Visa & Team Directory",
      "AI CFO Oracle chatbot access",
      "Priority support",
    ]},
    features: [
      { label: "Everything in Pro", href: R.receipts },
      { label: "Multi-tenant workspaces & team seats", href: R.enterprise },
      { label: "Amazon / Walmart / DoorDash sync", href: R.ingestion },
      { label: "Smart Visa & Team Directory", href: R.enterprise },
      { label: "AI CFO Oracle chatbot access", href: R.dashboard },
      { label: "Priority support" },
    ],
  },
  premium_shield: {
    displayName: "Premium Tax Shield · Elite",
    priceLabel: "$29.99/mo",
    accent: "#a855f7",
    accentBg: "rgba(168,85,247,0.14)",
    accentBorder: "rgba(168,85,247,0.5)",
    downgradeTo: { lookup_key: "business_monthly", label: "Move to Business", targetName: "Business", targetPrice: "$19.99/mo", losses: [
      "Real-Time Tax Shielding · Live IRS Risk Scoring",
      "IRS Audit-Shield · Composite Risk Score",
      "Real-Time Paycheck Stub Forecasting",
      "Quarterly Tax Vault · Auto-Reserving",
      "Mobile Camera Multi-shot Batch",
      "Cross-Device Batch Resume",
      "Pulsing Elite tier badge + launch confetti",
    ]},
    features: [
      // Everything from Business, explicitly listed:
      { label: "Unlimited OCR receipt scanning (GPT-5.2 Vision)", href: R.scan },
      { label: "IRS-compliant digital archiving", href: R.receipts },
      { label: "Smart categorization & space-age search", href: R.receipts },
      { label: "Export to PDF & CSV", href: R.receipts },
      { label: "Email inbox + SMS ingestion", href: R.ingestion },
      { label: "Multi-tenant workspaces & team seats", href: R.enterprise },
      { label: "Amazon / Walmart / DoorDash sync", href: R.ingestion },
      { label: "Smart Visa & Team Directory", href: R.enterprise },
      { label: "AI CFO Oracle chatbot access", href: R.dashboard },
      { label: "Priority support" },
      // Elite-only additions:
      { label: "Real-Time Tax Shielding · Live IRS Risk Scoring", href: R.auditShield },
      { label: "IRS Audit-Shield · Composite Risk Score", href: R.auditShield },
      { label: "Real-Time Paycheck Stub Forecasting (GPT-5.2 Vision)", href: R.paycheckOptimizer },
      { label: "Quarterly Tax Vault · Auto-Reserving", href: R.quarterlyVault },
      { label: "Mobile Camera Hardware Capture · Multi-shot Batch", href: R.paycheckOptimizer },
      { label: "Cross-Device Batch Resume (IndexedDB + Mongo sync)", href: R.paycheckOptimizer },
      { label: "Pulsing Elite tier badge + launch confetti", href: R.dashboard },
    ],
  },
};

export default function Billing() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: window.location.origin + "/billing",
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open portal");
      setLoading(false);
    }
  };

  const tierKey = (user?.subscription_tier || "pro").toLowerCase();
  const cfg = TIER_CONFIG[tierKey] || TIER_CONFIG.pro;
  const isPremium = tierKey === "premium_shield";
  const isPro = tierKey === "pro";
  const [downgrading, setDowngrading] = useState(false);
  const [showRetentionOffer, setShowRetentionOffer] = useState(false);
  const [retentionClaiming, setRetentionClaiming] = useState(false);
  const [retentionApplied, setRetentionApplied] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [paused, setPaused] = useState(!!user?.subscription_paused);
  const [resumesAt, setResumesAt] = useState(user?.subscription_resumes_at || null);
  const downgradeTarget = cfg.downgradeTo;
  const navigate = (typeof window !== "undefined") ? (href) => {
    if (!href) return;
    if (href.includes("#")) {
      const [path, hash] = href.split("#");
      if (window.location.pathname !== path) {
        window.location.assign(href);
      } else {
        const el = document.querySelector(`[data-testid=${hash}]`) || document.getElementById(hash);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      window.location.assign(href);
    }
  } : () => {};

  // Row click telemetry — fire-and-forget beacon.  Never blocks the deep-link.
  const trackFeatureClick = (feature, idx) => {
    try {
      api.post("/v1/billing/feature-click", {
        tier: tierKey,
        feature_label: feature.label,
        feature_index: idx,
        href: feature.href || null,
      }).catch(() => {});
    } catch {
      // silent
    }
  };

  const doDowngrade = async () => {
    if (!downgradeTarget || downgrading) return;
    setDowngrading(true);
    const toastId = toast.loading(`Prorating downgrade to ${downgradeTarget.targetName}…`);
    try {
      const { data } = await api.post("/payments/upgrade", {
        lookup_key: downgradeTarget.lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(`Downgraded to ${downgradeTarget.targetName} · prorated instantly`, {
        id: toastId,
        description: "Higher-tier features lock immediately. Re-upgrade is one click.",
      });
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Downgrade failed — try Stripe Portal", { id: toastId });
    } finally {
      setDowngrading(false);
      setShowDowngradeConfirm(false);
    }
  };

  // Retention Save Offer — claims the 30%/3mo Stripe coupon, keeps the user on tier.
  const claimRetentionOffer = async () => {
    if (retentionClaiming || retentionApplied) return;
    setRetentionClaiming(true);
    const toastId = toast.loading("Applying 30% × 3-month loyalty save…");
    try {
      const { data } = await api.post("/payments/apply-retention-coupon", {});
      if (data?.already_applied) {
        toast.message("Loyalty coupon already active on your account", { id: toastId });
      } else {
        toast.success("30% off · locked in for 3 months 🎁", {
          id: toastId,
          description: "Your next 3 invoices are discounted. Nothing else changed.",
        });
      }
      setRetentionApplied(true);
      setTimeout(() => setShowRetentionOffer(false), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply coupon — try Stripe Portal", { id: toastId });
    } finally {
      setRetentionClaiming(false);
    }
  };

  const declineRetentionAndContinueDowngrade = () => {
    setShowRetentionOffer(false);
    // Small delay so the retention modal unmounts before the confirm modal mounts
    setTimeout(() => setShowDowngradeConfirm(true), 120);
  };

  // Pause Subscription (Pro-tier only) — 30/60/90 day freeze via Stripe pause_collection
  const doPause = async (days) => {
    if (pausing) return;
    setPausing(true);
    const toastId = toast.loading(`Pausing billing for ${days} days…`);
    try {
      const { data } = await api.post("/payments/pause", { days });
      setPaused(true);
      setResumesAt(data?.resumes_at || null);
      toast.success(`Billing frozen for ${days} days`, {
        id: toastId,
        description: "Your receipt archive stays intact. Auto-resumes on the scheduled date.",
      });
      setShowPauseModal(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pause failed — try Stripe Portal", { id: toastId });
    } finally {
      setPausing(false);
    }
  };

  const doResume = async () => {
    if (pausing) return;
    setPausing(true);
    const toastId = toast.loading("Resuming billing…");
    try {
      await api.post("/payments/resume", {});
      setPaused(false);
      setResumesAt(null);
      toast.success("Billing resumed", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resume failed", { id: toastId });
    } finally {
      setPausing(false);
    }
  };

  const [upgrading, setUpgrading] = useState(false);
  const upgradeFleet = async () => {
    if (upgrading) return;
    setUpgrading(true);
    const toastId = toast.loading("Opening Enterprise Fleet checkout…");
    try {
      const { data } = await api.post("/payments/upgrade", {
        lookup_key: "enterprise_fleet_monthly",
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        toast.dismiss(toastId);
        window.location.href = data.checkout_url;
        return;
      }
      toast.success("Upgraded to Enterprise Fleet Command", { id: toastId });
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed — try Stripe Portal", { id: toastId });
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <DashboardLayout>
      <style>{`
        @keyframes fleetPulseRing {
          0%   { box-shadow: 0 0 0 0 rgba(6,182,212,0.65), 0 0 28px -8px rgba(6,182,212,0.4); }
          70%  { box-shadow: 0 0 0 14px rgba(6,182,212,0), 0 0 38px -4px rgba(6,182,212,0.6); }
          100% { box-shadow: 0 0 0 0 rgba(6,182,212,0), 0 0 28px -8px rgba(6,182,212,0.4); }
        }
        @keyframes stardustShimmer {
          0%         { opacity: 0; transform: translateX(-120%) skewX(-20deg); }
          40%, 60%   { opacity: 1; }
          100%       { opacity: 0; transform: translateX(120%) skewX(-20deg); }
        }
        .fleet-upgrade-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.22) 50%, transparent 100%);
          transform: translateX(-120%) skewX(-20deg);
          border-radius: inherit;
          pointer-events: none;
        }
        .fleet-upgrade-btn:hover::before {
          animation: stardustShimmer 1.8s ease-in-out infinite;
        }
        .fleet-upgrade-btn:hover {
          animation: fleetPulseRing 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          border-color: rgba(6,182,212,0.9) !important;
          background: rgba(6,182,212,0.14) !important;
        }
      `}</style>
      <div className="fade-up max-w-3xl">
        <div className="mb-8">
          <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-2">
            Account
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
            Billing
          </h1>
          <p className="text-slate-400 mt-2 text-sm">
            Manage your subscription, payment method and invoices in one place.
          </p>
        </div>

        <div
          className="subpage-glass-canvas subpage-hover-row p-8 mb-6 relative overflow-hidden"
          data-testid="billing-plan-card"
          data-tier={tierKey}
          style={isPremium ? {
            borderColor: cfg.accentBorder,
            boxShadow: `0 20px 40px -20px ${cfg.accentBorder}`,
          } : undefined}
        >
          {isPremium && (
            <span
              aria-hidden="true"
              className="absolute -top-24 -right-16 w-72 h-72 rounded-full pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(168,85,247,0.35), transparent 65%)",
                filter: "blur(20px)",
              }}
            />
          )}
          <div className="flex items-center gap-3 mb-2">
            <Sparkles size={16} style={{ color: cfg.accent }} />
            <span
              className="text-xs font-mono uppercase tracking-widest"
              style={{ color: cfg.accent }}
            >
              Billing Status Radar · Active Tier
            </span>
          </div>
          <div className="flex flex-wrap items-baseline gap-3 mt-2">
            <h2
              className="text-4xl font-bold text-white tracking-tight"
              data-testid="billing-tier-name"
            >
              {cfg.displayName}
            </h2>
            <span
              className="font-mono text-sm px-2 py-0.5 rounded-md"
              data-testid="billing-tier-price"
              style={{
                background: cfg.accentBg,
                border: `1px solid ${cfg.accentBorder}`,
                color: cfg.accent,
              }}
            >
              {cfg.priceLabel}
            </span>
            <div
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 text-xs font-semibold uppercase tracking-widest"
              data-testid="billing-status-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Active
            </div>
          </div>

          {/* Feature checklist */}
          <div
            className="mt-6"
            data-testid="billing-feature-checklist"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-3">
              What's unlocked on your plan
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {cfg.features.map((f, i) => {
                const clickable = !!f.href;
                const Tag = clickable ? "button" : "div";
                return (
                  <li key={i} className="list-none">
                    <Tag
                      onClick={clickable ? () => { trackFeatureClick(f, i); navigate(f.href); } : undefined}
                      data-testid={`billing-feature-${i}`}
                      data-href={f.href || ""}
                      className={`w-full flex items-start gap-2 text-slate-200 text-sm text-left rounded-md py-1 px-1.5 transition-colors ${clickable ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
                    >
                      <span
                        className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                        style={{
                          background: cfg.accentBg,
                          border: `1px solid ${cfg.accentBorder}`,
                        }}
                      >
                        <Check size={11} style={{ color: cfg.accent }} strokeWidth={3} />
                      </span>
                      <span className="leading-snug flex-1">{f.label}</span>
                      {clickable && (
                        <ExternalLink
                          size={11}
                          className="mt-1 shrink-0 opacity-40"
                          style={{ color: cfg.accent }}
                          aria-hidden="true"
                        />
                      )}
                    </Tag>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-6 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-4">
            <div>
              <div className="text-slate-500 text-xs uppercase tracking-widest">Account</div>
              <div className="text-white font-medium mt-1">{user?.name}</div>
              <div className="text-slate-400 text-sm font-mono">{user?.email}</div>
            </div>
            {!isPremium && (
              <a
                href="/paywall?upgrade=premium_shield_monthly"
                data-testid="billing-upgrade-link"
                className="self-start sm:self-center inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-950 hover:-translate-y-px transition-transform"
                style={{
                  background: "linear-gradient(135deg, #a855f7, #06B6D4)",
                  boxShadow: "0 8px 18px -8px rgba(168,85,247,0.6)",
                }}
              >
                Level up to Premium ↑
              </a>
            )}
            {isPremium ? (
              <button
                onClick={upgradeFleet}
                disabled={upgrading}
                data-testid="billing-enterprise-fleet-upgrade-btn"
                className="fleet-upgrade-btn self-start sm:self-center relative overflow-hidden inline-flex items-center gap-2 rounded-xl px-5 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-transform disabled:opacity-60 hover:-translate-y-px"
                style={{
                  background: "rgba(6,182,212,0.08)",
                  border: "1px solid rgba(6,182,212,0.6)",
                  boxShadow: "0 0 28px -8px rgba(6,182,212,0.4), inset 0 1px 0 rgba(6,182,212,0.12)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              >
                <span className="relative z-10">
                  {upgrading ? "Opening checkout…" : "⚡ UPGRADE TO ENTERPRISE FLEET COMMAND — $49.99/mo"}
                </span>
              </button>
            ) : downgradeTarget ? (
              <button
                onClick={() => { if (retentionApplied) { setShowDowngradeConfirm(true); } else { setShowRetentionOffer(true); } }}
                disabled={downgrading}
                data-testid="billing-downgrade-link"
                data-target={downgradeTarget.targetName.toLowerCase()}
                className="self-start sm:self-center inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 hover:text-white border border-slate-500/40 hover:border-slate-400/70 transition-colors"
                title={`Prorated downgrade — ${cfg.displayName} features lock immediately`}
              >
                {downgradeTarget.label}
              </button>
            ) : null}
          </div>
        </div>

        {/* Pro-tier only · Pause Subscription card */}
        {isPro && (
          <div className="subpage-glass-canvas subpage-hover-row p-8 mb-6" data-testid="pause-subscription-card">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center shrink-0">
                <PauseCircle size={20} className="text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white tracking-tight">Pause subscription</h3>
                <p className="text-slate-400 text-sm mt-1 max-w-md">
                  Freeze billing for 30, 60 or 90 days. Your full receipt archive and settings stay intact — billing auto-resumes on the scheduled date.
                </p>
                {paused && (
                  <div
                    data-testid="pause-active-badge"
                    className="mt-3 inline-flex items-center gap-2 rounded-md px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] font-mono uppercase tracking-widest"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                    Paused · resumes {resumesAt ? new Date(resumesAt).toLocaleDateString() : "soon"}
                  </div>
                )}
              </div>
              {paused ? (
                <button
                  onClick={doResume}
                  disabled={pausing}
                  data-testid="pause-resume-btn"
                  className="h-11 px-4 rounded-lg border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
                >
                  {pausing ? "Resuming…" : "Resume now"}
                </button>
              ) : (
                <button
                  onClick={() => setShowPauseModal(true)}
                  disabled={pausing}
                  data-testid="pause-open-modal-btn"
                  className="h-11 px-4 rounded-lg border border-amber-500/40 hover:bg-amber-500/10 text-amber-300 text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
                >
                  <PauseCircle size={14} /> Pause billing
                </button>
              )}
            </div>
          </div>
        )}

        {/* Retention Save Offer — intercepts every downgrade tap */}
        {showRetentionOffer && downgradeTarget && typeof document !== "undefined" && (
          <div
            data-testid="retention-save-modal"
            className="fixed inset-0 z-[9850] flex items-center justify-center px-4"
            style={{ background: "rgba(2,6,23,0.86)", backdropFilter: "blur(6px)" }}
            onClick={(e) => { if (e.target === e.currentTarget && !retentionClaiming) setShowRetentionOffer(false); }}
          >
            <div
              className="w-full max-w-md rounded-2xl p-6 relative overflow-hidden"
              style={{
                background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(6,25,55,0.98))",
                border: "1px solid rgba(253,224,71,0.45)",
                boxShadow: "0 30px 70px -20px rgba(253,224,71,0.35)",
              }}
            >
              <span
                aria-hidden="true"
                className="absolute -top-20 -right-16 w-72 h-72 rounded-full pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(253,224,71,0.35), transparent 65%)",
                  filter: "blur(20px)",
                }}
              />
              <div className="relative">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] text-yellow-300">
                  <Gift size={12} /> Loyalty Save · 3-Month Discount
                </div>
                <h3 className="text-white text-2xl font-bold mt-2 tracking-tight">
                  Wait — take 30% off for 3 months instead?
                </h3>
                <p className="text-slate-300 text-sm mt-3">
                  Keep every feature you already have on <span className="text-white font-semibold">{cfg.displayName}</span> and lock in <span className="text-yellow-300 font-mono">30% off</span> your next 3 invoices. One tap, no downgrade needed.
                </p>
                <ul className="mt-4 space-y-1.5 text-slate-200 text-sm">
                  <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-yellow-300 shrink-0" /> Applied via Stripe as a repeating coupon</li>
                  <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-yellow-300 shrink-0" /> Auto-expires after 3 billing cycles — no lock-in</li>
                  <li className="flex items-start gap-2"><Check size={14} className="mt-0.5 text-yellow-300 shrink-0" /> Every tier feature stays live · zero disruption</li>
                </ul>
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button
                    onClick={declineRetentionAndContinueDowngrade}
                    disabled={retentionClaiming}
                    data-testid="retention-decline-btn"
                    className="text-slate-300 hover:text-white text-xs font-mono uppercase tracking-widest px-3 py-2 rounded"
                  >
                    No thanks · continue downgrade
                  </button>
                  <button
                    onClick={claimRetentionOffer}
                    disabled={retentionClaiming}
                    data-testid="retention-accept-btn"
                    className={`rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-950 transition-transform ${retentionClaiming ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
                    style={{
                      background: "linear-gradient(135deg, #fde047, #f59e0b)",
                      boxShadow: "0 8px 18px -8px rgba(253,224,71,0.6)",
                    }}
                  >
                    {retentionClaiming ? "Applying…" : "Claim 30% off × 3 months"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pause Subscription modal — 30 / 60 / 90 day chooser */}
        {showPauseModal && typeof document !== "undefined" && (
          <div
            data-testid="pause-modal"
            className="fixed inset-0 z-[9820] flex items-center justify-center px-4"
            style={{ background: "rgba(2,6,23,0.86)", backdropFilter: "blur(6px)" }}
            onClick={(e) => { if (e.target === e.currentTarget && !pausing) setShowPauseModal(false); }}
          >
            <div
              className="w-full max-w-md rounded-2xl p-6"
              style={{
                background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(60,40,10,0.98))",
                border: "1px solid rgba(251,191,36,0.45)",
                boxShadow: "0 30px 70px -20px rgba(251,191,36,0.35)",
              }}
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-amber-300">
                Pause subscription
              </div>
              <h3 className="text-white text-xl font-bold mt-1">Freeze billing · Pick a duration</h3>
              <p className="text-slate-400 text-sm mt-2">
                We void every draft invoice while you're paused. Your receipt vault, categorizations, exports and integrations all stay live.
              </p>
              <div className="mt-5 grid grid-cols-3 gap-2">
                {[30, 60, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => doPause(d)}
                    disabled={pausing}
                    data-testid={`pause-days-${d}`}
                    className={`rounded-lg py-3 text-sm font-bold border transition-all ${pausing ? "opacity-60 cursor-wait" : "hover:-translate-y-px hover:bg-amber-500/10"}`}
                    style={{
                      background: "rgba(251,191,36,0.06)",
                      borderColor: "rgba(251,191,36,0.4)",
                      color: "#fde68a",
                    }}
                  >
                    {d} days
                  </button>
                ))}
              </div>
              <div className="mt-5 flex items-center justify-end">
                <button
                  onClick={() => setShowPauseModal(false)}
                  disabled={pausing}
                  data-testid="pause-cancel-btn"
                  className="text-slate-300 hover:text-white text-xs font-mono uppercase tracking-widest px-3 py-2 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Downgrade confirm dialog — retention loop, works for any tier */}
        {showDowngradeConfirm && downgradeTarget && typeof document !== "undefined" && (
          <div
            data-testid="billing-downgrade-confirm"
            data-target={downgradeTarget.targetName.toLowerCase()}
            className="fixed inset-0 z-[9800] flex items-center justify-center px-4"
            style={{ background: "rgba(2,6,23,0.86)", backdropFilter: "blur(6px)" }}
            onClick={(e) => { if (e.target === e.currentTarget && !downgrading) setShowDowngradeConfirm(false); }}
          >
            <div
              className="w-full max-w-md rounded-2xl p-6"
              style={{
                background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(30,15,60,0.98))",
                border: `1px solid ${cfg.accentBorder}`,
                boxShadow: `0 30px 70px -20px ${cfg.accentBorder}`,
              }}
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: cfg.accent }}>
                Downgrade to {downgradeTarget.targetName}
              </div>
              <h3 className="text-white text-xl font-bold mt-1">
                You'll lose {downgradeTarget.losses.length} features immediately
              </h3>
              <ul className="mt-4 space-y-1.5">
                {downgradeTarget.losses.map((f, i) => (
                  <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                    <span className="mt-1 w-1 h-1 rounded-full shrink-0" style={{ background: cfg.accent }} />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-5 text-slate-400 text-xs">
                Stripe will prorate the difference — you'll be charged the {downgradeTarget.targetName} rate ({downgradeTarget.targetPrice}) starting your next billing cycle. Re-upgrading later is one click.
              </div>
              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowDowngradeConfirm(false)}
                  disabled={downgrading}
                  data-testid="billing-downgrade-cancel"
                  className="text-slate-300 hover:text-white text-xs font-mono uppercase tracking-widest px-3 py-2 rounded"
                >
                  Keep {cfg.displayName.split(" ")[0]}
                </button>
                <button
                  onClick={doDowngrade}
                  disabled={downgrading}
                  data-testid="billing-downgrade-confirm-btn"
                  className={`rounded-md px-4 py-2 text-xs font-bold uppercase tracking-widest text-white transition-transform ${downgrading ? "opacity-70 cursor-wait" : "hover:-translate-y-px"}`}
                  style={{
                    background: "linear-gradient(135deg, #ef4444, #b91c1c)",
                    boxShadow: "0 8px 18px -8px rgba(239,68,68,0.6)",
                  }}
                >
                  {downgrading ? "Prorating…" : "Confirm downgrade"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="subpage-glass-canvas subpage-hover-row p-8 mb-6" data-testid="security-settings-card">
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center shrink-0">
              <ShieldCheck size={20} className="text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white tracking-tight">Security</h3>
              <p className="text-slate-400 text-sm mt-1 max-w-md">
                Add a second factor to your account. When enabled, sign-ins require a one-time code from your authenticator app.
              </p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-white font-medium">Enable Two-Factor Authentication (2FA)</div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mt-1">
                {twoFactor ? "Active · authenticator required at sign-in" : "Disabled · single-factor sign-in"}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={twoFactor}
              data-testid="two-factor-toggle"
              onClick={() => {
                setTwoFactor((v) => {
                  const next = !v;
                  toast[next ? "success" : "message"](
                    next ? "2FA activated · scan the QR from your authenticator app on next sign-in" : "2FA disabled"
                  );
                  return next;
                });
              }}
              className={`relative w-14 h-7 rounded-full transition-colors ${twoFactor ? "bg-emerald-500" : "bg-slate-600"}`}
              style={twoFactor ? { boxShadow: "0 0 22px rgba(16,185,129,0.7), 0 0 0 1px rgba(16,185,129,0.5) inset" } : undefined}
            >
              <span
                className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform"
                style={{ transform: twoFactor ? "translateX(28px)" : "translateX(0)" }}
              />
            </button>
          </div>
        </div>

        <div className="subpage-glass-canvas subpage-hover-row p-8 mb-6" data-testid="billing-portal-card">
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center shrink-0">
              <CreditCard size={20} className="text-emerald-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white tracking-tight">
                Self-service billing portal
              </h3>
              <p className="text-slate-400 text-sm mt-1 max-w-md">
                Update your card, download invoices, switch tiers, or cancel your
                subscription — all securely through Stripe.
              </p>
            </div>
            <button
              onClick={openPortal}
              disabled={loading}
              data-testid="open-portal-btn"
              className="btn-neon h-12 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center gap-2 disabled:opacity-60"
            >
              {loading ? "Opening…" : "Open portal"} <ExternalLink size={14} />
            </button>
          </div>
        </div>

        <div className="subpage-glass-canvas subpage-hover-row p-8" data-testid="billing-signout-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-white tracking-tight">Sign out</h3>
              <p className="text-slate-400 text-sm mt-1">
                End your session on this device. You can sign back in anytime.
              </p>
            </div>
            <button
              onClick={logout}
              data-testid="billing-logout-btn"
              className="h-11 px-4 rounded-lg border border-white/10 hover:border-red-500/40 hover:bg-red-500/5 hover:text-red-300 text-white text-sm flex items-center gap-2"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
